import { CoinIcon } from '../components/ui/CoinIcon';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import {
  FileText, CheckCircle, Zap, ArrowRight, BookOpen,
  TrendingUp, Users, Copy, Share2, AlertCircle, Plus,
  Calendar, BarChart2, Award, Clock, Settings
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = n => new Intl.NumberFormat('id-ID').format(n);

// Label display: dokumen lama tersimpan sebagai 'RPP', tampilkan sebagai 'Modul Ajar'
const DOC_TYPE_LABEL = { 'RPP': 'Modul Ajar' };
const getDocLabel = (type) => DOC_TYPE_LABEL[type] || type;

const DOC_TYPE_META = {
  'RPP':        { color: 'bg-blue-100 text-blue-700',   bar: 'bg-blue-500',   icon: '📋', desc: 'Modul Ajar' },
  'Modul Ajar': { color: 'bg-purple-100 text-purple-700', bar: 'bg-purple-500', icon: '📚', desc: 'Modul Pembelajaran' },
  'KKTP':       { color: 'bg-green-100 text-green-700',  bar: 'bg-green-500',  icon: '🎯', desc: 'Kriteria Ketercapaian' },
  'Promes':     { color: 'bg-orange-100 text-orange-700',bar: 'bg-orange-500', icon: '📅', desc: 'Program Semester' },
  'Prota':      { color: 'bg-pink-100 text-pink-700',    bar: 'bg-pink-500',   icon: '📆', desc: 'Program Tahunan' },
  'Jadwal':     { color: 'bg-cyan-100 text-cyan-700',    bar: 'bg-cyan-500',   icon: '🗓️', desc: 'Jadwal Mengajar' },
};

const QUICK_ACTIONS = [
  { type: 'Modul Ajar', grad: 'from-blue-500 to-blue-700',     icon: '📋' },
  { type: 'Modul Ajar', grad: 'from-purple-500 to-purple-700', icon: '📚' },
  { type: 'KKTP',       grad: 'from-green-500 to-green-700',   icon: '🎯' },
  { type: 'Promes',     grad: 'from-orange-400 to-orange-600', icon: '📅' },
  { type: 'Prota',      grad: 'from-pink-500 to-pink-700',     icon: '📆' },
  { type: 'Jadwal',     grad: 'from-cyan-500 to-cyan-700',     icon: '🗓️' },
];

export const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats]           = useState(null);
  const [documents, setDocuments]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [coinBalance, setCoinBalance] = useState(null);
  const [referral, setReferral]     = useState(null);
  const [copied, setCopied]         = useState(false);

  useEffect(() => { loadData(); }, [user]);

  const copyReferral = () => {
    if (!referral?.referral_code) return;
    navigator.clipboard.writeText(referral.referral_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareWA = () => {
    if (!referral?.referral_code) return;
    const appUrl = 'https://rencanaguru.com';
    const teks =
      `Halo! Aku pakai *Rencana Guru* buat bikin Modul Ajar & dokumen Kurikulum Merdeka otomatis pake AI. Hemat waktu banget!\n\n` +
      `Daftar sekarang dan pakai kode referralku, kamu langsung dapat *bonus 5 Coin Guru gratis*!\n\n` +
      `Kode referral: *${referral.referral_code}*\n` +
      `Link daftar: ${appUrl}/register`;
    window.location.href = `https://wa.me/?text=${encodeURIComponent(teks)}`;
  };

  const loadData = async () => {
    try {
      if (user?.role === 'super_admin') {
        const { data } = await axios.get(`${API}/analytics/dashboard`, { withCredentials: true });
        setStats(data);
      } else {
        const [docsRes, coinRes, referralRes] = await Promise.allSettled([
          axios.get(`${API}/documents`, { withCredentials: true }),
          axios.get(`${API}/coins/balance`, { withCredentials: true }),
          axios.get(`${API}/referral/info`, { withCredentials: true }),
        ]);
        if (docsRes.status === 'fulfilled') setDocuments(docsRes.value.data);
        if (coinRes.status === 'fulfilled') setCoinBalance(coinRes.value.data.coin_balance);
        if (referralRes.status === 'fulfilled') setReferral(referralRes.value.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // ── derived data ──
  const now = new Date();
  const docsThisMonth = documents.filter(d => {
    const c = new Date(d.created_at);
    return c.getMonth() === now.getMonth() && c.getFullYear() === now.getFullYear();
  });
  // Gabungkan 'RPP' (dokumen lama) ke 'Modul Ajar'
  const docsByType = Object.entries(
    documents.reduce((acc, d) => {
      const label = getDocLabel(d.document_type); // 'RPP' → 'Modul Ajar'
      acc[label] = (acc[label] || 0) + 1;
      return acc;
    }, {})
  ).sort((a, b) => b[1] - a[1]);

  const coinLow = coinBalance !== null && coinBalance <= 5;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-32">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" />
            <p className="text-slate-500 text-sm">Memuat data...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="dashboard-page" className="max-w-6xl mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>

        {/* ── HEADER ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-blue-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Selamat Datang, {user?.name?.split(' ')[0]}! 👋
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              {now.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
          {user?.role === 'guru' && (
            <Link to="/documents/generate"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-semibold text-sm shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
              style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
              <Zap size={16} /> Buat Dokumen Baru
            </Link>
          )}
        </div>

        {/* ════════════════════════════════════════
            GURU DASHBOARD
        ════════════════════════════════════════ */}
        {user?.role === 'guru' && (
          <>
            {/* ── ROW 1: Stats + Coin Card ── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">

              {/* Stat cards (2-col on left) */}
              <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: 'Total Dokumen', value: documents.length,       icon: FileText,    bg: 'bg-blue-50',   ic: 'text-blue-600' },
                  { label: 'Selesai',       value: documents.filter(d => d.status === 'completed').length, icon: CheckCircle, bg: 'bg-green-50',  ic: 'text-green-600' },
                  { label: 'Bulan Ini',     value: docsThisMonth.length,   icon: Calendar,    bg: 'bg-indigo-50', ic: 'text-indigo-600' },
                  { label: 'Jenis Dibuat',  value: new Set(documents.map(d => d.document_type)).size, icon: BookOpen, bg: 'bg-purple-50', ic: 'text-purple-600' },
                ].map(({ label, value, icon: Icon, bg, ic }) => (
                  <div key={label} className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${bg} mb-3`}>
                      <Icon size={18} className={ic} />
                    </div>
                    <div className="text-2xl font-bold text-blue-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{value}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{label}</div>
                  </div>
                ))}
              </div>

              {/* Coin Guru Card */}
              <div className={`rounded-2xl p-5 shadow-sm relative overflow-hidden ${coinLow ? 'bg-red-50 border border-red-200' : 'bg-white border border-slate-100'}`}>
                <div className="absolute top-0 right-0 w-28 h-28 rounded-full opacity-10"
                  style={{ background: coinLow ? '#EF4444' : '#3B82F6', transform: 'translate(30%, -30%)' }} />
                <div className="flex items-center gap-2 mb-3">
                  <CoinIcon size={22} />
                  <span className="text-sm font-bold text-slate-600">Coin Guru</span>
                  {coinLow && <span className="ml-auto flex items-center gap-1 text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full"><AlertCircle size={10}/> Hampir habis</span>}
                </div>
                <div className="text-4xl font-bold mb-1 relative z-10" style={{ fontFamily: "'Outfit', sans-serif", color: coinLow ? '#DC2626' : '#1E3A8A' }}>
                  {coinBalance ?? '—'}
                </div>
                <div className="text-xs text-slate-400 mb-4">koin tersisa · 1 koin = Rp 1.500</div>
                <div className="flex gap-2">
                  <Link to="/topup"
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-white text-xs font-bold transition-all hover:shadow-md"
                    style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
                    <Plus size={12} /> Isi Koin
                  </Link>
                  <Link to="/documents/generate"
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-blue-700 text-xs font-bold bg-blue-50 hover:bg-blue-100 transition-colors border border-blue-100">
                    <Zap size={12} /> Pakai
                  </Link>
                </div>
              </div>
            </div>

            {/* ── ROW 2: Quick Actions ── */}
            <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-5 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Buat Dokumen</h2>
                <span className="text-xs text-slate-400">6 jenis dokumen tersedia</span>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                {QUICK_ACTIONS.map(({ type, grad, icon }) => (
                  <Link key={type} to="/documents/generate"
                    className={`relative overflow-hidden rounded-xl p-3 text-white hover:shadow-lg hover:-translate-y-1 transition-all bg-gradient-to-br ${grad} flex flex-col items-center justify-center gap-1.5 aspect-square`}>
                    <div className="absolute top-0 right-0 w-12 h-12 rounded-full bg-white/10 -mr-4 -mt-4" />
                    <span className="text-2xl">{icon}</span>
                    <div className="text-xs font-bold text-center leading-tight relative z-10">{type}</div>
                  </Link>
                ))}
              </div>
            </div>

            {/* ── ROW 3: Breakdown + Referral ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">

              {/* Dokumen per Jenis */}
              <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-5">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart2 size={16} className="text-blue-500" />
                  <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Distribusi Dokumen</h2>
                </div>
                {docsByType.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                    <BarChart2 size={32} className="mb-2 opacity-30" />
                    <p className="text-sm">Belum ada dokumen dibuat</p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {Object.keys(DOC_TYPE_META).filter(type => type !== 'RPP').map(type => {
                      // Gabungkan count 'RPP' (lama) ke dalam 'Modul Ajar'
                      const count = documents.filter(d =>
                        d.document_type === type || (type === 'Modul Ajar' && d.document_type === 'RPP')
                      ).length;
                      const pct = documents.length > 0 ? Math.round((count / documents.length) * 100) : 0;
                      const meta = DOC_TYPE_META[type];
                      return (
                        <div key={type}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="text-base">{meta.icon}</span>
                              <span className="text-sm font-semibold text-slate-700">{getDocLabel(type)}</span>
                            </div>
                            <span className="text-sm font-bold text-slate-600">{count} <span className="text-xs text-slate-400 font-normal">dok</span></span>
                          </div>
                          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${meta.bar}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Referral Card */}
              <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-5">
                <div className="flex items-center gap-2 mb-1">
                  <Award size={16} className="text-yellow-500" />
                  <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Program Referral</h2>
                </div>
                <p className="text-xs text-slate-400 mb-4">Ajak teman dan dapatkan koin gratis</p>

                {referral?.referral_code ? (
                  <>
                    {/* Code box */}
                    <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl px-4 py-3 text-center mb-4">
                      <div className="text-xs text-slate-400 mb-1">Kode Referral Anda</div>
                      <div className="text-xl font-bold tracking-widest text-blue-700" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {referral.referral_code}
                      </div>
                    </div>

                    {/* Stats row */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {[
                        { label: 'Teman Bergabung', value: referral.referral_count || 0, icon: Users },
                        { label: 'Koin Didapat', value: (referral.referral_count || 0) * 10, icon: null, emoji: null, isCoin: true },
                        { label: 'Bonus/Teman', value: '+10 koin', icon: null, emoji: '🎁' },
                      ].map(({ label, value, icon: Icon, emoji, isCoin }) => (
                        <div key={label} className="bg-blue-50 rounded-xl p-2.5 text-center">
                          <div className="text-lg font-bold text-blue-800 flex items-center justify-center gap-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                            {isCoin ? <CoinIcon size={18} /> : emoji ? <span>{emoji}</span> : null}
                            {value}
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5 leading-tight">{label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-2">
                      <button onClick={copyReferral}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold transition-all border ${copied ? 'bg-green-50 border-green-200 text-green-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                        <Copy size={13} /> {copied ? 'Tersalin!' : 'Salin Kode'}
                      </button>
                      <button onClick={shareWA}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-white text-sm font-bold transition-all hover:shadow-md"
                        style={{ background: '#25D366' }}>
                        <Share2 size={13} /> Share via WA
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-6 text-slate-400 text-sm">Kode referral tidak tersedia</div>
                )}
              </div>
            </div>

            {/* ── ROW 4: Recent Documents ── */}
            <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-slate-400" />
                  <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Dokumen Terbaru</h2>
                </div>
                <Link to="/documents" className="text-blue-600 hover:text-blue-700 text-sm font-semibold flex items-center gap-1">
                  Lihat Semua <ArrowRight size={14} />
                </Link>
              </div>

              {documents.length === 0 ? (
                <div className="text-center py-16 px-6">
                  <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
                    <FileText size={28} className="text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-slate-700 mb-2">Belum Ada Dokumen</h3>
                  <p className="text-slate-400 text-sm mb-6">Mulai buat dokumen pertama Anda dengan bantuan AI!</p>
                  <Link to="/documents/generate"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-semibold text-sm"
                    style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
                    <Zap size={15} /> Buat Dokumen Pertama
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-slate-50">
                  {documents.slice(0, 6).map((doc) => {
                    const meta = DOC_TYPE_META[doc.document_type] || { color: 'bg-slate-100 text-slate-600', icon: '📄' };
                    return (
                      <div key={doc.id} className="flex items-center gap-4 px-6 py-3.5 hover:bg-slate-50 transition-colors">
                        <span className="text-xl flex-shrink-0">{meta.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${meta.color}`}>{doc.document_type}</span>
                            <span className="font-semibold text-slate-800 text-sm truncate">{doc.subject_name}</span>
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5">
                            Kelas {doc.kelas} · {doc.jenjang} · {doc.semester} {doc.tahun_ajaran}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className="text-xs text-slate-400 hidden sm:block">
                            {new Date(doc.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })}
                          </span>
                          <span className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${doc.status === 'completed' ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                            {doc.status === 'completed' ? <><CheckCircle size={10}/> Selesai</> : 'Draft'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* ════════════════════════════════════════
            SUPER ADMIN DASHBOARD
        ════════════════════════════════════════ */}
        {user?.role === 'super_admin' && (
          <>
            {/* ── Stat Cards ── */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {[
                { label: 'Total Guru',       value: stats?.total_guru      ?? '—', icon: Users,      bg: 'bg-blue-50',   ic: 'text-blue-600',   sub: 'pengguna terdaftar' },
                { label: 'Total Dokumen',    value: stats?.total_documents ?? '—', icon: FileText,   bg: 'bg-purple-50', ic: 'text-purple-600', sub: 'semua waktu' },
                { label: 'Dokumen Bulan Ini',value: stats?.docs_this_month ?? '—', icon: TrendingUp, bg: 'bg-orange-50', ic: 'text-orange-600', sub: 'bulan berjalan' },
                { label: 'Mata Pelajaran',   value: stats?.total_subjects  ?? '—', icon: BookOpen,   bg: 'bg-green-50',  ic: 'text-green-600',  sub: 'tersedia' },
              ].map(({ label, value, icon: Icon, bg, ic, sub }) => (
                <div key={label} className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">{label}</p>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${bg}`}>
                      <Icon size={18} className={ic} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-blue-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{value}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{sub}</p>
                </div>
              ))}
            </div>

            {/* ── Pendapatan Card ── */}
            <div className="relative overflow-hidden rounded-2xl p-5 mb-6 text-white"
              style={{ background: 'linear-gradient(135deg, #1E3A8A 0%, #2563EB 60%, #3B82F6 100%)' }}>
              <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-white/5 -mr-16 -mt-16" />
              <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-white/5 -ml-10 -mb-10" />
              <div className="relative z-10">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <p className="text-blue-200 text-xs font-semibold uppercase tracking-wider">
                    💰 Pendapatan Bulan {new Date().toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })}
                  </p>
                  <span className="text-xs bg-white/10 border border-white/20 rounded-full px-2.5 py-0.5 text-blue-200">
                    {stats?.transaksi_bulan_ini ?? 0} transaksi
                  </span>
                </div>

                {/* Grid pendapatan */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-white/10 border border-white/20 rounded-xl p-3 text-center">
                    <p className="text-xs text-blue-300 mb-1">Gross</p>
                    <p className="text-lg font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      Rp {fmt(stats?.pendapatan_bulan_ini ?? 0)}
                    </p>
                    <p className="text-xs text-blue-300 mt-0.5">pendapatan kotor</p>
                  </div>
                  <div className="bg-red-500/20 border border-red-400/30 rounded-xl p-3 text-center">
                    <p className="text-xs text-red-300 mb-1">Biaya Midtrans</p>
                    <p className="text-lg font-bold text-red-300" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      − Rp {fmt(stats?.biaya_midtrans_bulan_ini ?? 0)}
                    </p>
                    <p className="text-xs text-red-400 mt-0.5">0.7% per transaksi</p>
                  </div>
                  <div className="bg-green-500/20 border border-green-400/30 rounded-xl p-3 text-center">
                    <p className="text-xs text-green-300 mb-1">Pendapatan Bersih</p>
                    <p className="text-lg font-bold text-green-300" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      Rp {fmt(stats?.pendapatan_bersih_bulan_ini ?? 0)}
                    </p>
                    <p className="text-xs text-green-400 mt-0.5">setelah biaya</p>
                  </div>
                </div>

                {/* Total semua waktu */}
                <div className="border-t border-white/10 pt-3 flex items-center justify-between">
                  <p className="text-xs text-blue-300">Total semua waktu ({stats?.total_transaksi ?? 0} transaksi)</p>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-blue-300">Gross: <strong className="text-white">Rp {fmt(stats?.pendapatan_total ?? 0)}</strong></span>
                    <span className="text-xs text-red-300">Fee: <strong>− Rp {fmt(stats?.biaya_midtrans_total ?? 0)}</strong></span>
                    <span className="text-xs text-green-300">Bersih: <strong className="text-green-200">Rp {fmt(stats?.pendapatan_bersih_total ?? 0)}</strong></span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Aktivitas Bulan Ini + Ekonomi Koin ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">

              {/* Aktivitas Guru Bulan Ini */}
              <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-5">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart2 size={16} className="text-blue-500" />
                  <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Aktivitas Bulan Ini</h2>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    {
                      label: 'Guru Aktif',
                      value: stats?.guru_aktif_bulan_ini ?? '—',
                      sub: 'buat dokumen bulan ini',
                      emoji: '✍️',
                      color: 'bg-blue-50 border-blue-100',
                      val: 'text-blue-800',
                    },
                    {
                      label: 'Guru Baru',
                      value: stats?.guru_baru_bulan_ini ?? '—',
                      sub: 'daftar bulan ini',
                      emoji: '🆕',
                      color: 'bg-green-50 border-green-100',
                      val: 'text-green-800',
                    },
                    {
                      label: 'Koin Rendah',
                      value: stats?.guru_koin_rendah ?? '—',
                      sub: 'saldo ≤ 5 koin',
                      emoji: '⚠️',
                      color: 'bg-red-50 border-red-100',
                      val: 'text-red-700',
                    },
                  ].map(({ label, value, sub, emoji, color, val }) => (
                    <div key={label} className={`rounded-xl border p-3 text-center ${color}`}>
                      <div className="text-2xl mb-1">{emoji}</div>
                      <div className={`text-2xl font-bold ${val}`} style={{ fontFamily: "'Outfit', sans-serif" }}>{value}</div>
                      <div className="text-xs text-slate-500 font-semibold mt-0.5">{label}</div>
                      <div className="text-xs text-slate-400 leading-tight mt-0.5">{sub}</div>
                    </div>
                  ))}
                </div>

                {/* Guru koin rendah CTA */}
                {(stats?.guru_koin_rendah ?? 0) > 0 && (
                  <Link to="/users"
                    className="mt-4 flex items-center justify-between px-4 py-2.5 bg-red-50 border border-red-100 rounded-xl text-sm hover:bg-red-100 transition-colors">
                    <span className="text-red-700 font-semibold">
                      {stats.guru_koin_rendah} guru hampir kehabisan koin — topup sekarang
                    </span>
                    <ArrowRight size={14} className="text-red-500 flex-shrink-0" />
                  </Link>
                )}
              </div>

              {/* Ekonomi Koin */}
              <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-5">
                <div className="flex items-center gap-2 mb-4">
                  <CoinIcon size={18} />
                  <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Ekonomi Coin Guru</h2>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {[
                    {
                      label: 'Koin Tersisa',
                      value: fmt(stats?.total_coin_beredar ?? 0),
                      sub: 'total saldo aktif semua guru',
                      bg: 'bg-yellow-50 border-yellow-100',
                      val: 'text-yellow-800',
                    },
                    {
                      label: 'Koin Terpakai',
                      value: fmt(stats?.total_coin_digunakan ?? 0),
                      sub: 'total koin untuk generate dokumen',
                      bg: 'bg-slate-50 border-slate-200',
                      val: 'text-slate-700',
                    },
                  ].map(({ label, value, sub, bg, val }) => (
                    <div key={label} className={`rounded-xl border p-4 ${bg}`}>
                      <div className={`text-2xl font-bold ${val}`} style={{ fontFamily: "'Outfit', sans-serif" }}>{value}</div>
                      <div className="text-xs font-semibold text-slate-600 mt-1">{label}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{sub}</div>
                    </div>
                  ))}
                </div>
                <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl">
                  <p className="text-xs text-blue-700">
                    <strong>Nilai koin tersisa:</strong>{' '}
                    Rp {fmt((stats?.total_coin_beredar ?? 0) * 2000)} ·
                    Total digunakan: Rp {fmt((stats?.total_coin_digunakan ?? 0) * 2000)}
                  </p>
                </div>
              </div>
            </div>

            {/* ── Dokumen Populer + Quick Links ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

              {/* Dokumen Terpopuler */}
              <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-5">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart2 size={16} className="text-purple-500" />
                  <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Dokumen Paling Banyak Dibuat</h2>
                </div>
                {(stats?.docs_by_type ?? []).length === 0 ? (
                  <div className="text-center py-8 text-slate-400 text-sm">Belum ada dokumen</div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {/* Gabungkan 'RPP' (lama) dan 'Modul Ajar' (baru) menjadi satu entry */}
                    {Object.values(
                      (stats?.docs_by_type ?? []).reduce((acc, { type, count }) => {
                        const label = getDocLabel(type); // 'RPP' → 'Modul Ajar'
                        if (acc[label]) {
                          acc[label].count += count;
                        } else {
                          acc[label] = { type: label, count };
                        }
                        return acc;
                      }, {})
                    )
                    .sort((a, b) => b.count - a.count)
                    .map(({ type, count }) => {
                      const meta = DOC_TYPE_META[type] || { icon: '📄', bar: 'bg-slate-400' };
                      const max = Math.max(...Object.values(
                        (stats?.docs_by_type ?? []).reduce((acc, { type: t, count: c }) => {
                          const l = getDocLabel(t);
                          acc[l] = (acc[l] || 0) + c;
                          return acc;
                        }, {})
                      )) || 1;
                      const pct = Math.round((count / max) * 100);
                      return (
                        <div key={type}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span>{meta.icon}</span>
                              <span className="text-sm font-semibold text-slate-700">{getDocLabel(type)}</span>
                            </div>
                            <span className="text-sm font-bold text-slate-600">{fmt(count)} <span className="text-xs text-slate-400 font-normal">dok</span></span>
                          </div>
                          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${meta.bar}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Quick Links Admin */}
              <div className="flex flex-col gap-3">
                {[
                  { to: '/users',    label: 'Manajemen Guru',    desc: 'Lihat daftar guru & topup koin manual', icon: Users,      grad: 'from-blue-500 to-blue-700' },
                  { to: '/subjects', label: 'Mata Pelajaran',    desc: 'Tambah/edit/hapus mata pelajaran',      icon: BookOpen,   grad: 'from-purple-500 to-purple-700' },
                  { to: '/settings', label: 'Pengaturan Sistem', desc: 'Anthropic API key & paket koin',         icon: TrendingUp, grad: 'from-slate-600 to-slate-800' },
                ].map(({ to, label, desc, icon: Icon, grad }) => (
                  <Link key={to} to={to}
                    className={`relative overflow-hidden rounded-2xl px-5 py-4 text-white bg-gradient-to-r ${grad} hover:shadow-lg hover:-translate-y-0.5 transition-all flex items-center gap-4`}>
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                      <Icon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm">{label}</div>
                      <div className="text-white/70 text-xs mt-0.5">{desc}</div>
                    </div>
                    <ArrowRight size={16} className="text-white/60 flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}

      </div>
    </DashboardLayout>
  );
};
